import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, finalize, of, Subject, startWith, debounceTime, switchMap, shareReplay, timeout } from 'rxjs';
import { environment } from '../../environments/environment';
import { MdAiPanelService } from './md-ai-panel.service';

export interface AgentChatRequest {
  message: string;
  sessionId?: string;
  companyId?: string;
  webSearchEnabled?: boolean;
  /**
   * Files are staged (not yet permanent). We send the opaque `stagingId` plus
   * the metadata the agent echoes back at promote-on-confirm time. NO permanent
   * URL exists yet — the blob is promoted only if the proposal is confirmed.
   */
  attachments?: Array<{ stagingId: string; name: string; contentType: string; size: number; previewUrl?: string }>;
}

/**
 * Response from the staging upload endpoint. The file lives in a temporary
 * container (TTL ~48h) and is only copied to permanent storage when the
 * resulting proposal is confirmed. `stagingId` is the ONLY field the client
 * persists; `previewUrl` is a short-lived read-only SAS for thumbnails and may
 * expire before `expiresAt`.
 */
export interface StagedAttachment {
  stagingId: string;
  name: string;
  contentType: string;
  size: number;
  previewUrl: string;
  expiresAt: string;
}

export interface AgentChatResponse {
  sessionId: string;
  reply: string;
  status: 'completed' | 'pending_approval' | 'awaiting_confirmation' | 'error' | 'processing';
  actions?: AgentAction[];
  /**
   * Bulk AP batch id — present when status === 'processing'. Poll getBatchStatus().
   *
   * Replaces `preview`/`previews`/`jobId`. The agent no longer returns editable review cards:
   * drafts land in the platform and are reviewed in the AP module, which already has the full
   * form, validation and approval routing.
   */
  batchId?: string;
  /** Number of invoices in the batch. */
  total?: number;
}

/** Step-chip state for one invoice. There is no `denied`: Deny went with the review card. */
export type BatchItemState =
  | 'queued'
  | 'creating'
  | 'created'
  | 'skipped'
  | 'needs-attention'
  | 'failed';

export interface BatchItem {
  id: string;
  fileName: string;
  state: BatchItemState;
  serviceRequestId?: string;
  apNumber?: string;
  /** The existing invoice a skip collided with. */
  duplicateOf?: string;
  /** One sentence for the step chip. Always present for needs-attention and failed. */
  reason?: string;
  /**
   * Every reason this invoice was flagged, most actionable first, with `reason` as the first
   * entry.
   *
   * <p>Show all of them. The chip shows `reason` and this is what its "+N more" control opens.
   * Absent from an agent that predates the field, which is why callers go through
   * `batchItemFlags()` rather than reading it directly.
   */
  flags?: string[];
  /**
   * How the draft was built — where its lines came from, how much history was behind them.
   *
   * <p>Absent on an agent that predates it, and on any item that did not get as far as a draft.
   */
  provenance?: {
    vendorMatchMethod?: string;
    vendorRunnerUp?: string;
    similarCount?: number;
    similarUsedApNumber?: string;
    similarLatestDate?: string;
    billType?: string;
    memoRule?: string;
    lineSource?: 'history' | 'rules' | 'model' | 'none';
    storeNumber?: string;
    entityVia?: string;
  };
}

/**
 * Progress for a bulk AP batch.
 *
 * Unlike the OCR job poll it replaces, this is the **whole current state** on every call rather
 * than "what is new since the last poll" — so a dropped response loses nothing and the step-chip
 * list can be rendered from scratch after a page reload.
 */
export interface BatchStatus {
  batchId: string;
  status: 'running' | 'completed' | 'cancelled';
  total: number;
  items: BatchItem[];
  summary: Record<BatchItemState, number>;
  createdAt: string;
  completedAt: string | null;
  /** What the model cost for this batch. Absent when the agent is not counting. */
  costUsd?: number;
}

export interface AgentAction {
  id: string;
  sessionId: string;
  module: string;
  action: string;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  humanReadableSummary: string;
  status: string;
  estimatedImpact?: string;
  payload?: Record<string, unknown>;
  createdAt: string;
}

export interface AgentSessionSummary {
  id: string;
  userRole: string;
  companyId: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * What wrote a conversation message.
 *
 * <p>The two batch kinds arrive with no request behind them: a bulk upload keeps working after
 * the user navigates away, and writes what it did into the transcript. That is how a batch's
 * outcome survives a closed tab.
 *
 * <p>Absent on an agent that predates the field, which is why `kind` is optional and callers
 * treat a missing value as `'chat'`.
 */
export type AgentMessageKind = 'chat' | 'batch_item' | 'batch_summary';

export interface AgentConversationMessage {
  /** Autoincrement. Poll from the highest one seen to fetch only what is new. */
  id?: number;
  role: 'user' | 'assistant';
  content: string;
  kind?: AgentMessageKind;
  /** The batch this came from, for the two batch kinds. */
  refId?: string;
  createdAt?: string;
  attachments?: Array<{ name: string; url: string; contentType: string; size: number }>;
}

// --- "Similar past AP requests" (Recall this transaction) ---

export type ResolvedVendorMatch = 'exact' | 'fuzzy' | 'none';
export type SimilarApMatchType = 'exact_vendor' | 'fuzzy_vendor';

export interface SimilarApRequestBody {
  vendorId?: string;
  vendorName?: string;
  subsidiaryId?: string;
  /** When editing an existing draft, pass its id so it can't suggest itself. */
  excludeApRequestId?: string;
  maxResults?: number;     // default 5
  lookbackMonths?: number; // default 18
}

export interface SimilarApLineItem {
  chartOfAccountId: string;
  chartOfAccountName?: string;
  subsidiaryId: string;
  storeId?: string;
  storeName?: string;
  amount: number;
  description?: string;
}

export interface SimilarApMatch {
  apRequestId: string;
  apNumber: string;            // == apRequestId
  score: number;               // 0–100
  matchType: SimilarApMatchType;
  vendorMatchScore?: number;   // 0–95
  subsidiaryMatched: boolean;
  matchReasons: string[];
  vendorId?: string;
  vendorName: string;
  subsidiaryId?: string;
  subsidiaryName?: string;
  invoiceNumber?: string;
  invoiceAmount: number;
  invoiceDate?: string;        // ISO UTC
  dueDate?: string;            // ISO UTC
  status: string;
  createdOn?: string;          // ISO UTC
  createdByName: string;
  lineItems: SimilarApLineItem[];
}

export interface SimilarApResponse {
  query: {
    companyId?: string;
    vendorId?: string;
    vendorName?: string;
    subsidiaryId?: string;
    resolvedVendorMatch: ResolvedVendorMatch;
  };
  totalCandidates: number;
  matches: SimilarApMatch[];
}

export interface AgentStatus {
  status: string;
  service: string;
  tokenBudget: {
    costUsd: number;
    limitUsd: number;
    inputTokens: number;
    outputTokens: number;
    withinBudget: boolean;
  };
  scheduler: {
    tasks: number;
  };
  /**
   * What the agent will accept.
   *
   * <p>Read rather than hardcoded, so raising the cap for a load run does not leave the upload
   * control contradicting the service. Absent on an agent that predates it — callers fall back
   * to the documented default.
   */
  limits?: {
    maxInvoicesPerBatch: number;
    maxRunningBatchesPerUser: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AgentService {

  private ctrlUrl = environment.agentUrl || `${environment.apiUrl}/v1/agent`;

  private _inboxLoading = new BehaviorSubject<boolean>(false);
  inboxLoading$ = this._inboxLoading.asObservable();

  private _chatLoading = new BehaviorSubject<boolean>(false);
  chatLoading$ = this._chatLoading.asObservable();

  private readonly _refreshInbox$ = new Subject<void>();

  /** Reactive inbox count — refreshes on demand */
  readonly inboxActions$ = this._refreshInbox$.pipe(
    startWith(void 0),
    debounceTime(100),
    switchMap(() => this.getInbox()),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  constructor(private http: HttpClient, private mdAiPanelSvc: MdAiPanelService) {}

  /** 403 from either the Backend ([RequireAiAllowed], body `code: 'AiDisabled'`) or the Agent's own gate (body `error: 'ai_disabled'`). */
  private isAiDisabledError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse) || error.status !== 403) return false;
    const body = error.error;
    return body?.code === 'AiDisabled' || body?.error === 'ai_disabled';
  }

  // --- Chat ---

  chat(request: AgentChatRequest): Observable<AgentChatResponse> {
    this._chatLoading.next(true);
    return this.http.post<AgentChatResponse>(`${this.ctrlUrl}/chat`, request).pipe(
      // OCR + Claude can take a while; cap it so a hung request surfaces an
      // error instead of leaving the panel stuck with no response forever.
      timeout(90000),
      catchError(error => {
        console.error('[AgentService] Chat error:', error);
        // The interceptor already toasts error.error.message for any 403 (including this one —
        // "AI features are turned off in your AI Preferences."); closing the panel here is the
        // one thing a generic toast can't do on its own.
        if (this.isAiDisabledError(error)) {
          this.mdAiPanelSvc.closePanel();
        }
        const timedOut = error?.name === 'TimeoutError';
        return of({
          sessionId: '',
          reply: timedOut
            ? 'The assistant took too long to respond. Please try again.'
            : 'Failed to reach the AI agent. Please try again.',
          status: 'error' as const,
        });
      }),
      finalize(() => this._chatLoading.next(false))
    );
  }

  // --- AI Inbox ---

  getInbox(): Observable<{ actions: AgentAction[]; count: number }> {
    this._inboxLoading.next(true);
    return this.http.get<{ actions: AgentAction[]; count: number }>(`${this.ctrlUrl}/actions`).pipe(
      catchError(error => {
        console.error('[AgentService] Inbox error:', error);
        return of({ actions: [], count: 0 });
      }),
      finalize(() => this._inboxLoading.next(false))
    );
  }

  approveAction(actionId: string, reason?: string): Observable<any> {
    return this.http.patch(`${this.ctrlUrl}/actions/${actionId}/approve`, { reason }).pipe(
      catchError(error => {
        console.error('[AgentService] Approve error:', error);
        return of(null);
      })
    );
  }

  denyAction(actionId: string, reason?: string): Observable<any> {
    return this.http.patch(`${this.ctrlUrl}/actions/${actionId}/deny`, { reason }).pipe(
      catchError(error => {
        console.error('[AgentService] Deny error:', error);
        return of(null);
      })
    );
  }

  draftAction(actionId: string): Observable<any> {
    return this.http.patch(`${this.ctrlUrl}/actions/${actionId}/draft`, {}).pipe(
      catchError(error => {
        console.error('[AgentService] Draft error:', error);
        return of(null);
      })
    );
  }

  /**
   * Poll a bulk AP batch.
   *
   * Returns the whole current state, so the caller renders from scratch each time. That is a
   * deliberate change from the OCR job endpoint this replaces, which delivered each card once
   * and therefore lost work permanently if a response was dropped or the page reloaded.
   */
  getBatchStatus(batchId: string): Observable<BatchStatus | null> {
    return this.http.get<BatchStatus>(`${this.ctrlUrl}/batch/${batchId}`).pipe(
      catchError(error => {
        console.error('[AgentService] Batch status error:', error);
        // null tells the caller to stop polling rather than loop on a dead batch.
        return of(null);
      }),
    );
  }

  /**
   * Cancel a batch. Invoices already in flight finish; nothing new is started.
   *
   * Drafts already created are left alone — the agent has no delete capability, and an unwanted
   * draft is deleted in the AP module by someone with permission to do it.
   */
  cancelBatch(batchId: string): Observable<BatchStatus | null> {
    return this.http.post<BatchStatus>(`${this.ctrlUrl}/batch/${batchId}/cancel`, {}).pipe(
      catchError(() => of(null)),
    );
  }

  refreshInbox(): void {
    this._refreshInbox$.next();
  }

  // --- Action History ---

  getActionHistory(limit: number = 50): Observable<{ actions: AgentAction[]; count: number }> {
    return this.http.get<{ actions: AgentAction[]; count: number }>(
      `${this.ctrlUrl}/actions/history`,
      { params: { limit: limit.toString() } }
    ).pipe(
      catchError(error => {
        console.error('[AgentService] History error:', error);
        return of({ actions: [], count: 0 });
      })
    );
  }

  // --- Session ---

  getSession(sessionId: string): Observable<any> {
    return this.http.get(`${this.ctrlUrl}/session/${sessionId}`);
  }

  /**
   * Bulk uploads started in a session, so a returning conversation can show them again.
   *
   * The transcript alone does not carry them: an invoice batch's progress lives in the agent's
   * batch tables, so without this the invoices someone uploaded disappear from the chat the
   * moment they navigate away, even though the work carries on and finishes.
   */
  getSessionBatches(sessionId: string): Observable<{ batches: BatchStatus[]; count: number }> {
    return this.http.get<{ batches: BatchStatus[]; count: number }>(
      `${this.ctrlUrl}/sessions/${sessionId}/batches`
    );
  }

  /** The caller's past sessions for the role they are acting as — what History lists. */
  getSessions(limit = 50): Observable<{ sessions: AgentSessionSummary[]; count: number }> {
    return this.http.get<{ sessions: AgentSessionSummary[]; count: number }>(
      `${this.ctrlUrl}/sessions`, { params: { limit } }
    );
  }

  /**
   * The session's messages, or only those after `afterId`.
   *
   * <p>The incremental form is what a session watching a running batch polls with: the batch
   * writes its lines into the transcript as it goes, and re-fetching the whole thing every 2.5
   * seconds to find one new line is the kind of thing that works until a conversation is long.
   */
  getConversation(
    sessionId: string,
    afterId?: number,
  ): Observable<{ messages: AgentConversationMessage[]; count: number }> {
    return this.http.get<{ messages: AgentConversationMessage[]; count: number }>(
      `${this.ctrlUrl}/session/${sessionId}/conversation`,
      afterId ? { params: { after: afterId } } : {}
    );
  }

  // --- Attachments ---

  /**
   * Upload an attachment to STAGING (temporary blob storage, TTL ~48h).
   * The file is NOT permanent — it is promoted to permanent storage server-side
   * only when the resulting proposal is confirmed. The client persists the
   * returned `stagingId` and sends it back with the chat message.
   *
   * Uses field name 'File' (uppercase) matching the platform API specification.
   * @param file The file to upload
   * @param module Target module: 'AP', 'EXPENSE', 'RM' (defaults to 'RM' for backward compat)
   */
  uploadAttachment(file: File, module: 'AP' | 'EXPENSE' | 'RM' = 'RM'): Observable<StagedAttachment> {
    const endpointMap: Record<string, string> = {
      AP: '/v1/AccountsPayable/upload/attachment',
      EXPENSE: '/v1/Expense/upload/attachment',
      RM: '/v1/ServiceRequest/upload/attachment',
    };
    const path = endpointMap[module] || endpointMap['RM'];

    const formData = new FormData();
    formData.append('File', file, file.name);
    return this.http.post<StagedAttachment>(
      `${environment.apiUrl}${path}`,
      formData
    );
  }

  /**
   * Similar past AP requests for the same vendor (best-effort).
   * `company`/`role` headers are attached by CustomHttpInterceptor — do NOT
   * send `companyId` in the body. Fails quiet to an empty result so the AP
   * form is never blocked on this panel.
   */
  apSimilar(body: SimilarApRequestBody): Observable<SimilarApResponse> {
    return this.http.post<SimilarApResponse>(`${this.ctrlUrl}/ap/similar`, body).pipe(
      catchError(error => {
        console.error('[AgentService] apSimilar error:', error);
        return of({
          query: { resolvedVendorMatch: 'none' as const },
          totalCandidates: 0,
          matches: [],
        });
      }),
    );
  }

  // --- Status ---

  getStatus(): Observable<AgentStatus> {
    return this.http.get<AgentStatus>(`${this.ctrlUrl}/status`);
  }
}
